// src/components/game/GridCell.js
import React, { useMemo } from 'react';
import { GRID_W, GRID_H } from '../constants/gameConstants';

const hexToRgba = (hex, alpha = 0.5) => {
    if (!hex || hex === 'transparent') return 'transparent';
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const GridCell = React.memo(({
    owner,
    x,
    y,
    isSetupSelected,
    isSelected,
    isTarget,
    isSelectableEnemy,
    isSelectableEmpty,
    isMyTurn,
    isSetupPhase,
    onCellClick,
    players,
    myPlayerIndex,
    gameGrid,
}) => {
    const isMe = owner === myPlayerIndex;
    const chosenBeforeMe = isSetupPhase && owner !== -1 && owner < myPlayerIndex;
    const chosenAfterMe = isSetupPhase && owner !== -1 && owner > myPlayerIndex;
    const cellIndex = y * GRID_W + x;

    // 1. Renk Hesaplaması
    const backgroundColor = useMemo(() => {
        let colorHex;
        if (isSetupPhase) {
            if (owner === -1 || chosenAfterMe) colorHex = 'transparent';
            else if (chosenBeforeMe) colorHex = '#4B5563';
            else colorHex = players[owner]?.color || '#666';
        } else {
            if (owner === -1) colorHex = 'transparent';
            else colorHex = players[owner]?.color || '#666';
        }
        return hexToRgba(colorHex, 0.5);
    }, [owner, players, isSetupPhase, chosenAfterMe, chosenBeforeMe]);

    // 2. Cephe Hattı Sınır Hesaplaması + Tehlike Seviyesi
    const borderInfo = useMemo(() => {
        if (owner === -1 || !gameGrid) return { classes: '', dangerLevel: 0 };

        const topOwner = y > 0 ? gameGrid[(y - 1) * GRID_W + x] : owner;
        const rightOwner = x < GRID_W - 1 ? gameGrid[y * GRID_W + (x + 1)] : owner;
        const bottomOwner = y < GRID_H - 1 ? gameGrid[(y + 1) * GRID_W + x] : owner;
        const leftOwner = x > 0 ? gameGrid[y * GRID_W + (x - 1)] : owner;

        const isEnemyBorder = (neighborOwner) => neighborOwner !== -1 && neighborOwner !== owner;

        let classes = [];
        let dangerCount = 0;

        if (isEnemyBorder(topOwner)) {
            classes.push('border-t-enemy');
            dangerCount++;
        }
        if (isEnemyBorder(rightOwner)) {
            classes.push('border-r-enemy');
            dangerCount++;
        }
        if (isEnemyBorder(bottomOwner)) {
            classes.push('border-b-enemy');
            dangerCount++;
        }
        if (isEnemyBorder(leftOwner)) {
            classes.push('border-l-enemy');
            dangerCount++;
        }

        return {
            classes: classes.join(' '),
            dangerLevel: dangerCount // 0-4 arası: kaç yönden düşman var
        };
    }, [owner, x, y, gameGrid]);

    // 3. Tehlike Overlay (4 yönden kuşatılmışsa ekstra efekt)
    const dangerOverlay = useMemo(() => {
        if (borderInfo.dangerLevel === 0) return null;

        // Tehlike seviyesine göre opacity artışı
        const intensity = Math.min(borderInfo.dangerLevel * 0.15, 0.6);
        
        return {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `radial-gradient(circle, rgba(255, 68, 68, ${intensity}), transparent 70%)`,
            mixBlendMode: 'screen',
            zIndex: 1
        };
    }, [borderInfo.dangerLevel]);

    // 4. Seçim Efektleri
    const overlayStyle = useMemo(() => {
        if (isSetupSelected) return {
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.6), transparent)',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.8), inset 0 0 20px rgba(59, 130, 246, 0.4)',
            border: '2px solid rgba(59, 130, 246, 0.9)',
        };
        if (isSelected) return { boxShadow: `inset 0 0 0 4px rgba(34, 197, 94, 0.8)` };
        if (isTarget) return { boxShadow: `inset 0 0 0 4px rgba(239, 68, 68, 0.8)` };
        if (isSelectableEmpty) return { boxShadow: `inset 0 0 0 2px rgba(96, 165, 250, 0.6)` };
        if (isSelectableEnemy) return { boxShadow: `inset 0 0 0 2px rgba(251, 191, 36, 0.6)` };
        return null;
    }, [isSetupSelected, isSelected, isTarget, isSelectableEnemy, isSelectableEmpty]);

    const cellStyle = useMemo(() => ({
        position: 'relative',
        backgroundColor,
        transition: 'all 0.2s ease',
        transform: 'translate3d(0, 0, 0)',
        willChange: 'transform',
        isolation: 'isolate', // Blend mode için
    }), [backgroundColor]);

    const hoverClass = (isMyTurn || isSetupPhase) ? 'hover:backdrop-brightness-125 cursor-pointer' : 'cursor-not-allowed';

    // 🔥 Yüksek tehlike seviyesi için ekstra class
    const dangerClass = borderInfo.dangerLevel >= 3 ? 'high-danger-cell' : '';

    return (
        <div
            className={`w-full h-full ${hoverClass} ${borderInfo.classes} ${dangerClass}`}
            style={cellStyle}
            onClick={() => onCellClick(x, y)}
        >
            {/* Tehlike Overlay */}
            {dangerOverlay && (
                <div style={dangerOverlay} />
            )}

            {/* Seçim Overlay */}
            {overlayStyle && (
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2, ...overlayStyle }} />
            )}
        </div>
    );
}, (prev, next) => {
    return (
        prev.owner === next.owner &&
        prev.isSelected === next.isSelected &&
        prev.isTarget === next.isTarget &&
        prev.isSetupSelected === next.isSetupSelected &&
        prev.isSelectableEmpty === next.isSelectableEmpty &&
        prev.isSelectableEnemy === next.isSelectableEnemy &&
        prev.isMyTurn === next.isMyTurn &&
        prev.isSetupPhase === next.isSetupPhase &&
        prev.myPlayerIndex === next.myPlayerIndex &&
        prev.gameGrid === next.gameGrid
    );
});